import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { cacheGet, cachedFetch, invalidateCache } from "@/lib/cache";
import { useAuth } from "@/context/AuthContext";
import { fechaHora, fechaSolo } from "@/lib/constants";
import { DOCS_LIBERAR, DOC_LABEL, GRUPOS, LIBRES_LIBERADA, MAX_OBSERVACIONES, RUTA, VALIDACION, descargarFdd08, estadoHorma, puedeEditarHormas, puedePedirModificacion } from "@/lib/hormas";
import { SectionCard, Field } from "@/components/modelo/SectionCard";
import FechaInput from "@/components/FechaInput";
import LazyImage from "@/components/LazyImage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertTriangle, Box, Check, ChevronLeft, ClipboardCheck, FileSpreadsheet, FileText, History, Image as ImageIcon, Layers, Pencil, Power, Route, Upload,
} from "lucide-react";

// Misma rejilla en vista y edición: etiquetas de un renglón, columnas alineadas.
const GRID_FICHA = "grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4";
const TITULO_GRUPO = "text-xs font-bold uppercase tracking-wide text-slate-500 mb-3 pb-1.5 border-b border-slate-100";
const fechaDMA = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

const ACCION_COLOR = {
  alta: "bg-blue-500", dar_alta: "bg-blue-600", aprobar: "bg-emerald-500", liberar: "bg-emerald-600", rechazar: "bg-red-500",
  modificar: "bg-amber-500", desactivar: "bg-slate-500", activar: "bg-slate-500", edicion: "bg-slate-300", foto: "bg-slate-300", documento: "bg-blue-300",
};

export default function HormaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [h, setH] = useState(() => cacheGet(`/hormas/${id}`) ?? null);
  const [dlg, setDlg] = useState(null); // {tipo: alta|modificar|activa, motivo}
  const [busy, setBusy] = useState(false);
  const archivoRefs = useRef({});
  const editar = puedeEditarHormas(user);
  const pedirModificacion = puedePedirModificacion(user);

  const cargar = () => cachedFetch(`/hormas/${id}`, setH);
  useEffect(() => {
    cargar().catch(() => navigate("/hormas"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const ejecutar = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      invalidateCache("/hormas");
      await cargar();
      if (ok) toast.success(ok);
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!h) return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <Skeleton className="h-24 rounded-xl mb-6" />
      <Skeleton className="h-72 rounded-xl" />
    </div>
  );

  const est = estadoHorma(h);
  const liberada = h.etapa === "liberada";
  const dadaDeAlta = h.etapa === "dada_de_alta";
  const enUso = liberada || dadaDeAlta;   // ya se usa en los modelos: medidas y fotos solo con "Pedir modificación"
  const enModificacion = h.etapa === "en_modificacion";
  const pasoActual = liberada ? 2 : dadaDeAlta ? 1 : 0;
  const faltanDocs = Object.entries(DOCS_LIBERAR).filter(([, [, col]]) => !h[col]).map(([, [label]]) => label);
  // Último motivo de modificación (para mostrarlo mientras se corrige).
  const ultimaModificacion = enModificacion ? (h.eventos || []).find((e) => e.accion === "modificar") : null;

  // Dar de alta: con medidas y fotos. Si viene de una modificación, se pide el motivo de la corrección.
  const darDeAlta = () => (enModificacion ? setDlg({ tipo: "alta", motivo: "" })
    : ejecutar(() => api.post(`/hormas/${id}/alta`), "Horma revisada: ya se puede elegir en los modelos"));
  // Liberar: con la horma revisada y el archivo de Pull Over & Prueba de Calce adjunto.
  const liberar = () => ejecutar(() => api.post(`/hormas/${id}/aprobar`), "Horma liberada");

  const confirmarDlg = async () => {
    const motivo = (dlg.motivo || "").trim();
    const llamadas = {
      alta: () => api.post(`/hormas/${id}/alta`, { motivo }),
      modificar: () => api.post(`/hormas/${id}/modificar`, { motivo }),
      activa: () => api.post(`/hormas/${id}/activa`, { activa: !h.activa, motivo }),
    };
    const textos = { alta: "Horma revisada con la corrección", modificar: "Modificación pedida: se avisó a Desarrollo", activa: h.activa ? "Horma desactivada" : "Horma reactivada" };
    if (await ejecutar(llamadas[dlg.tipo], textos[dlg.tipo])) setDlg(null);
  };

  // Fotos (foto_externa / foto_interna) y archivo de Pull Over & Prueba de Calce (carta_pull_over).
  const subirArchivo = (tipo, ok) => {
    const input = archivoRefs.current[tipo];
    if (!input) return;
    input.onchange = (e) => {
      const file = e.target.files[0];
      e.target.value = "";
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", tipo);
      ejecutar(() => api.post(`/hormas/${id}/archivos`, fd, { headers: { "Content-Type": "multipart/form-data" } }), ok);
    };
    input.click();
  };
  // Historial de archivos de Pull Over & Prueba de Calce (vigentes y anteriores).
  const docsRuta = (h.archivos || []).filter((a) => DOC_LABEL[a.tipo]);

  const generar = async () => {
    setBusy(true);
    try { await descargarFdd08(h); } catch (e) { toast.error("No se pudo generar el formato"); } finally { setBusy(false); }
  };

  const guardarFicha = async (d) => {
    const cambios = {};
    GRUPOS.flatMap((g) => g.campos).concat([["notas"]]).forEach(([k]) => {
      if ((d[k] ?? "") !== (h[k] ?? "")) cambios[k] = d[k];
    });
    if (!Object.keys(cambios).length) return;
    await api.patch(`/hormas/${id}`, { datos: cambios });
    invalidateCache("/hormas");
    toast.success("Cambios guardados");
    await cargar();
  };

  const guardarValidacion = async (d) => {
    await api.patch(`/hormas/${id}`, { datos: { validacion: d } });
    invalidateCache("/hormas");
    toast.success("Validación guardada");
    await cargar();
  };
  const val = h.validacion || {};

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <button onClick={() => navigate("/hormas")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4 transition-colors">
        <ChevronLeft size={16} /> Volver a hormas
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-11 w-11 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0"><Box size={20} /></div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight truncate">{h.nombre}</h1>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${est.cls}`} data-testid="horma-estado">{est.label}</span>
            </div>
            <div className="text-sm text-slate-500 font-mono">{h.codigo}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={generar} disabled={busy} data-testid="generar-fdd08">
            <FileSpreadsheet size={16} className="mr-1.5" /> Generar formato FDD-08
          </Button>
          {pedirModificacion && enUso && (
            <Button variant="outline" onClick={() => setDlg({ tipo: "modificar", motivo: "" })} data-testid="modificar-horma">
              <Pencil size={15} className="mr-1.5" /> Pedir modificación
            </Button>
          )}
          {editar && (
            <Button variant="ghost" className="text-slate-500" onClick={() => setDlg({ tipo: "activa", motivo: "" })} data-testid="activa-horma">
              <Power size={15} className="mr-1.5" /> {h.activa ? "Desactivar" : "Reactivar"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          {/* Ruta */}
          <Card className="border-slate-200 shadow-sm" data-testid="horma-ruta">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-100">
              <Route size={17} className="text-slate-400" />
              <h3 className="font-head font-bold tracking-tight">Ruta de la horma</h3>
            </div>
            <div className="p-5">
              <ol className="grid grid-cols-3 gap-3">
                {RUTA.map((r, i) => {
                  const hecho = i < pasoActual || liberada;
                  const actual = !liberada && i === pasoActual;
                  return (
                    <li key={r.key} className={`rounded-lg border px-3 h-14 flex items-center gap-2.5 ${actual ? (enModificacion ? "border-orange-300 bg-orange-50" : "border-amber-300 bg-amber-50") : hecho ? "border-emerald-200 bg-emerald-50" : "border-slate-200"}`}>
                      <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${hecho ? "bg-emerald-500 text-white" : actual ? (enModificacion ? "bg-orange-400 text-white" : "bg-amber-400 text-white") : "bg-slate-100 text-slate-400"}`}>
                        {hecho ? <Check size={13} strokeWidth={3} /> : i + 1}
                      </span>
                      <span className={`text-sm leading-tight ${actual ? "font-semibold text-slate-900" : hecho ? "text-slate-700" : "text-slate-400"}`}>
                        {actual && enModificacion ? "En modificación" : r.label}
                      </span>
                    </li>
                  );
                })}
              </ol>
              {liberada ? (
                <p className="mt-4 text-sm text-emerald-700" data-testid="horma-liberada-info">
                  ✓ Liberada el {fechaHora(h.liberada_en)}{h.liberada_por_nombre && ` por ${h.liberada_por_nombre}`}.
                  {h.activa ? " Se puede elegir en los modelos." : " Está inactiva: no se puede elegir en los modelos."}
                </p>
              ) : dadaDeAlta ? (
                <div className="mt-4" data-testid="horma-dada-de-alta">
                  <p className="text-sm text-blue-700">
                    Revisado: {h.activa ? "ya se puede elegir en los modelos." : "está inactiva, no se puede elegir en los modelos."} Para liberarla, adjunta el archivo de Pull Over & Prueba de Calce.
                  </p>
                  <div className="mt-3 space-y-2">
                    {Object.entries(DOCS_LIBERAR).map(([tipo, [label, col]]) => {
                      const actualDoc = (h.archivos || []).find((a) => a.id === h[col]);
                      return (
                        <div key={tipo} className="flex items-center gap-2 text-sm rounded-lg border border-slate-200 px-3 py-2" data-testid={`doc-${tipo}`}>
                          <FileText size={14} className={actualDoc ? "text-emerald-600" : "text-slate-300"} />
                          <span className="font-medium text-slate-700 shrink-0">{label}</span>
                          {actualDoc ? (
                            <a href={fileUrl(actualDoc.id)} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700 truncate">{actualDoc.nombre}</a>
                          ) : <span className="text-slate-400">Sin adjuntar</span>}
                          {editar && (
                            <button onClick={() => subirArchivo(tipo, `Archivo de ${label} adjunto`)} disabled={busy} className="ml-auto text-xs text-blue-600 flex items-center gap-1 hover:text-blue-700 shrink-0" data-testid={`subir-${tipo}`}>
                              <Upload size={12} /> {actualDoc ? "Cambiar" : "Adjuntar"}
                            </button>
                          )}
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.xls,.xlsx,.doc,.docx" className="hidden" ref={(el) => (archivoRefs.current[tipo] = el)} />
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-slate-500 mr-auto">
                      {faltanDocs.length ? `Falta adjuntar: ${faltanDocs.join(" y ")}.` : "Archivo adjunto: ya se puede liberar."}
                    </span>
                    {editar && (
                      <Button onClick={liberar} disabled={busy || faltanDocs.length > 0} data-testid="liberar-horma">
                        <Check size={15} className="mr-1" /> Liberar horma
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {ultimaModificacion && (
                    <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 flex items-start gap-2 text-sm text-orange-900" data-testid="horma-en-modificacion">
                      <AlertTriangle size={16} className="text-orange-600 mt-0.5 shrink-0" />
                      <span>
                        {ultimaModificacion.descripcion} <span className="text-orange-700/80">({ultimaModificacion.usuario_nombre} · {fechaHora(ultimaModificacion.fecha)})</span>
                        {h.modelos.length > 0 && <><br />Los {h.modelos.length} modelo(s) que la usan están detenidos hasta que se vuelva a dar de alta.</>}
                      </span>
                    </div>
                  )}
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-slate-500 mr-auto">
                      {enModificacion ? "Corrige las medidas o fotos y da de alta anotando el motivo de la corrección." : "Para dar de alta se necesitan central, largo, recio, cintura, empeine y las dos fotos."}
                    </span>
                    {editar && (
                      <Button onClick={darDeAlta} disabled={busy} data-testid="dar-alta">
                        <Check size={15} className="mr-1" /> Dar de alta
                      </Button>
                    )}
                  </div>
                </>
              )}
              {docsRuta.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100" data-testid="docs-ruta">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Historial de Pull Over & Prueba de Calce</div>
                  <div className="space-y-1">
                    {docsRuta.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 text-[12px] text-slate-500 flex-wrap">
                        <FileText size={12} className="text-slate-400" />
                        <span className="font-medium text-slate-600">{DOC_LABEL[a.tipo]}</span>
                        <a href={fileUrl(a.id)} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700 truncate max-w-[220px]">{a.nombre}</a>
                        <span className="font-mono text-slate-400">v{a.version}</span>
                        <span className="text-slate-400">· {a.subido_por_nombre} · {fechaHora(a.subido_en)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Ficha */}
          <SectionCard
            title="Ficha de la horma" icon={Layers} section="horma" canEdit={editar} testid="horma-ficha"
            onSave={guardarFicha}
            renderEdit={{
              initial: () => ({ ...h }),
              form: (d, set) => (
                <div className="space-y-5">
                  {enUso && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      La horma está {liberada ? "liberada" : "revisada"}: solo se cambian ubicación, fecha marcada y notas. Para lo demás se pide una modificación.
                    </p>
                  )}
                  {GRUPOS.map((g) => (
                    <div key={g.titulo}>
                      <div className={TITULO_GRUPO}>{g.titulo}</div>
                      <div className={GRID_FICHA}>
                        {g.campos.map(([k, label, tipo, ayuda]) => {
                          const bloqueado = enUso && !LIBRES_LIBERADA.includes(k);
                          return (
                            <div key={k} className="min-w-0 space-y-1.5">
                              <Label className="block truncate" title={ayuda || label}>{label}</Label>
                              {tipo === "auto" ? (
                                <div className="h-9 px-3 flex items-center rounded-md border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500" title={ayuda}>
                                  {fechaDMA(d[k]) || "Se asigna sola"}
                                </div>
                              ) : tipo === "fecha" ? (
                                <FechaInput value={d[k] || ""} disabled={bloqueado} onChange={(e) => set({ ...d, [k]: e.target.value })} />
                              ) : (
                                <Input value={d[k] || ""} disabled={bloqueado} onChange={(e) => set({ ...d, [k]: e.target.value })} data-testid={`horma-${k}`} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <div className="space-y-1.5">
                    <Label>Notas</Label>
                    <Textarea value={d.notas || ""} onChange={(e) => set({ ...d, notas: e.target.value })} rows={3} />
                  </div>
                </div>
              ),
            }}
          >
            <div className="space-y-5">
              {GRUPOS.map((g) => (
                <div key={g.titulo}>
                  <div className={TITULO_GRUPO}>{g.titulo}</div>
                  <div className={GRID_FICHA}>
                    {g.campos.map(([k, label, tipo, ayuda]) => (
                      <div key={k} className="min-w-0">
                        <div className="text-xs text-slate-400 uppercase tracking-wide truncate" title={ayuda || label}>{label}</div>
                        <div className="text-sm text-slate-800 mt-0.5 truncate" title={h[k] || ""}>{(tipo ? fechaDMA(h[k]) : h[k]) || "—"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {h.notas && <Field label="Notas" value={h.notas} />}
            </div>
          </SectionCard>

          {/* Validación FDD-08 (parte de abajo del formato) */}
          <SectionCard
            title="Validación (formato FDD-08)" icon={ClipboardCheck} section="validacion" canEdit={editar} testid="horma-validacion"
            onSave={guardarValidacion}
            renderEdit={{
              initial: () => JSON.parse(JSON.stringify(val)),
              form: (d, set) => {
                const cambiar = (cuadro, campo, valor) => set({ ...d, [cuadro]: { ...(d[cuadro] || {}), [campo]: valor } });
                return (
                  <div className="grid md:grid-cols-2 gap-5">
                    {VALIDACION.map((c) => (
                      <div key={c.key} className="rounded-lg border border-slate-200 p-3" data-testid={`val-${c.key}`}>
                        <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{c.titulo}</div>
                        <div className="space-y-1.5">
                          {c.puntos.map(([p, label]) => (
                            <div key={p} className="flex items-center gap-2 text-sm">
                              <span className="flex-1 text-slate-700">{label}</span>
                              {[["conforme", "Conforme", "bg-emerald-600 border-emerald-600"], ["no_conforme", "No conforme", "bg-red-600 border-red-600"]].map(([v, txt, on]) => (
                                <button key={v} type="button" data-testid={`val-${c.key}-${p}-${v}`}
                                  onClick={() => cambiar(c.key, p, d[c.key]?.[p] === v ? null : v)}
                                  className={`px-2 py-1 rounded-md text-xs border ${d[c.key]?.[p] === v ? `${on} text-white` : "bg-white text-slate-600 border-slate-200"}`}>
                                  {txt}
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                        <Textarea className="mt-2.5 text-sm" rows={3} maxLength={MAX_OBSERVACIONES} placeholder="Observaciones"
                          value={d[c.key]?.observaciones || ""} onChange={(e) => cambiar(c.key, "observaciones", e.target.value)} data-testid={`val-${c.key}-obs`} />
                      </div>
                    ))}
                  </div>
                );
              },
            }}
          >
            <div className="grid md:grid-cols-2 gap-5">
              {VALIDACION.map((c) => (
                <div key={c.key}>
                  <div className={TITULO_GRUPO}>{c.titulo}</div>
                  <div className="space-y-1 text-sm">
                    {c.puntos.map(([p, label]) => (
                      <div key={p} className="flex items-center justify-between gap-2">
                        <span className="text-slate-600">{label}</span>
                        {val[c.key]?.[p] === "conforme" ? <span className="text-xs font-semibold text-emerald-700">Conforme</span>
                          : val[c.key]?.[p] === "no_conforme" ? <span className="text-xs font-semibold text-red-600">No conforme</span>
                          : <span className="text-xs text-slate-400">—</span>}
                      </div>
                    ))}
                  </div>
                  {val[c.key]?.observaciones && <p className="mt-1.5 text-xs text-slate-500 whitespace-pre-wrap">{val[c.key].observaciones}</p>}
                </div>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-slate-400">Se imprime en la parte de abajo del formato FDD-08. No detiene la liberación; se puede llenar o corregir aunque la horma ya esté liberada.</p>
          </SectionCard>

          {/* Bitácora */}
          <Card className="border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-100">
              <History size={17} className="text-slate-400" />
              <h3 className="font-head font-bold tracking-tight">Bitácora</h3>
            </div>
            <div className="p-5 space-y-3" data-testid="horma-bitacora">
              {h.eventos.map((e) => (
                <div key={e.id} className="flex gap-3 text-sm">
                  <span className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${ACCION_COLOR[e.accion] || "bg-slate-300"}`} />
                  <div className="min-w-0">
                    <div className="text-slate-700">{e.descripcion}</div>
                    <div className="text-[11px] text-slate-400">{e.usuario_nombre} · {fechaHora(e.fecha)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="lg:col-span-4 space-y-6">
          {/* Fotos */}
          <Card className="border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-100">
              <ImageIcon size={17} className="text-slate-400" />
              <h3 className="font-head font-bold tracking-tight">Fotos</h3>
            </div>
            <div className="p-5 space-y-4">
              {[["foto_externa", "Lado externo", h.foto_externa_id], ["foto_interna", "Lado interno", h.foto_interna_id]].map(([lado, label, fid]) => (
                <div key={lado}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
                    {editar && !enUso && (
                      <button onClick={() => subirArchivo(lado, "Foto subida")} disabled={busy} className="text-xs text-blue-600 flex items-center gap-1 hover:text-blue-700" data-testid={`subir-${lado}`}>
                        <Upload size={12} /> {fid ? "Cambiar" : "Subir"}
                      </button>
                    )}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" ref={(el) => (archivoRefs.current[lado] = el)} />
                  </div>
                  <div className="aspect-[4/3] rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden">
                    {fid ? (
                      <a href={fileUrl(fid)} target="_blank" rel="noreferrer" className="w-full h-full">
                        <LazyImage src={fileUrl(fid)} alt={label} containerClassName="w-full h-full bg-slate-50" imgClassName="object-contain" />
                      </a>
                    ) : <ImageIcon className="text-slate-300" size={28} />}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Modelos */}
          <Card className="border-slate-200 shadow-sm">
            <div className="px-5 py-3.5 border-b border-slate-100">
              <h3 className="font-head font-bold tracking-tight">Modelos que la usan</h3>
              <p className="text-xs text-slate-400 mt-0.5">{h.modelos.length} modelo{h.modelos.length === 1 ? "" : "s"}</p>
            </div>
            <div className="divide-y divide-slate-100" data-testid="horma-modelos">
              {h.modelos.length === 0 && <p className="px-5 py-4 text-sm text-slate-400">Ningún modelo la usa todavía.</p>}
              {h.modelos.map((m) => (
                <Link key={m.id} to={`/modelos/${m.id}`} className="block px-5 py-2.5 hover:bg-slate-50">
                  <div className="text-sm font-medium text-slate-800 truncate">{m.nombre}{m.depurado && <span className="ml-1.5 text-[10px] text-slate-400">(depurado)</span>}</div>
                  <div className="text-[11px] text-slate-400 truncate">{[m.sku, m.linea, m.coleccion_nombre].filter(Boolean).join(" · ")}</div>
                </Link>
              ))}
            </div>
          </Card>

          {/* Versiones */}
          <Card className="border-slate-200 shadow-sm">
            <div className="px-5 py-3.5 border-b border-slate-100">
              <h3 className="font-head font-bold tracking-tight">Versiones</h3>
              <p className="text-xs text-slate-400 mt-0.5">Una por cada liberación (se identifican por fecha).</p>
            </div>
            <div className="p-5 space-y-2 text-sm" data-testid="horma-versiones">
              {h.versiones.length === 0 && <p className="text-slate-400">Aún no se ha liberado.</p>}
              {h.versiones.map((v, i) => (
                <div key={v.id} className="flex items-center justify-between gap-2">
                  <span className="text-slate-700">{fechaSolo(v.liberada_en)}{i === 0 && liberada && <span className="ml-1.5 text-[11px] font-semibold text-emerald-600">Vigente</span>}</span>
                  <span className="text-[11px] text-slate-400 truncate">{v.liberada_por_nombre}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          {dlg && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {dlg.tipo === "alta" ? "Dar de alta la horma corregida" : dlg.tipo === "modificar" ? "Pedir modificación de la horma" : h.activa ? "Desactivar horma" : "Reactivar horma"}
                </DialogTitle>
                <DialogDescription>
                  {dlg.tipo === "alta" && "Anota qué se corrigió y por qué. Queda en la bitácora de la horma."}
                  {dlg.tipo === "modificar" && `La horma queda "En modificación" y deja de poder elegirse. ${h.modelos.length ? `Los ${h.modelos.length} modelo(s) que la usan se detienen hasta que se vuelva a dar de alta. ` : ""}Para liberarla otra vez se adjunta de nuevo el archivo de Pull Over & Prueba de Calce. Se avisa a Desarrollo para que la corrija.`}
                  {dlg.tipo === "activa" && (h.activa ? "Una horma inactiva no se puede elegir y detiene a los modelos que la usan." : "Vuelve a estar disponible si está revisada o liberada.")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5">
                <Label>{dlg.tipo === "alta" ? "Motivo de la corrección" : "Motivo"} (mínimo 10 caracteres)</Label>
                <Textarea value={dlg.motivo} onChange={(e) => setDlg({ ...dlg, motivo: e.target.value })} rows={3} data-testid="horma-motivo" />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDlg(null)}>Cancelar</Button>
                <Button onClick={confirmarDlg} disabled={busy || dlg.motivo.trim().length < 10} data-testid="horma-motivo-confirmar">Confirmar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
